import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getProtected } from "../../network/ApiService";
import { API_END_POINTS } from "../../network/apiEndPoint";

const ViewFinance = (props) => {
  const { showModal, setShowModal, assetId } = props;
  const [financialData, setFinancialData] = useState({
    depreciation: {},
    expenses: {},
    summary: {}
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('depreciation');
  const { t } = useTranslation();

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!showModal) return;
      
      setLoading(true);
      try {
        const response = await getProtected(API_END_POINTS.financeSummary);
        if (response) {
          setFinancialData(response);
        }
      } catch (error) {
        console.error("Error fetching financial data:", error);
        setError(t('viewFinance.errorMessage') || 'Failed to load financial data');
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialData();
  }, [showModal, t]);

  const renderDepreciationReport = () => (
    <div className="space-y-4">
      <div className="pb-4">
        <h3 className="text-xl font-semibold text-gray-800">{t('viewFinance.depreciation.title')}</h3>
        <p className="text-sm text-gray-600">{t('viewFinance.depreciation.subtitle')}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white -lg shadow p-4">
          <div className="flex items-center mb-2">
            <div className="w-10 h-10 -full bg-blue-100 flex items-center justify-center text-blue-600 mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700">{t('viewFinance.depreciation.totalValue.title')}</h4>
              <span className="text-lg font-bold text-gray-900">
                ${financialData?.depreciation?.total_asset_value?.toLocaleString() || 0}
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white -lg shadow p-4">
          <div className="flex items-center mb-2">
            <div className="w-10 h-10 -full bg-blue-100 flex items-center justify-center text-blue-600 mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700">{t('viewFinance.depreciation.averageValue.title')}</h4>
              <span className="text-lg font-bold text-gray-900">
                ${financialData?.depreciation?.average_asset_value?.toLocaleString() || 0}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white -lg shadow p-4">
          <div className="flex items-center mb-2">
            <div className="w-10 h-10 -full bg-blue-100 flex items-center justify-center text-blue-600 mr-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700">{t('viewFinance.depreciation.totalAssets.title')}</h4>
              <span className="text-lg font-bold text-gray-900">
                {financialData?.summary?.total_assets || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderExpensesReport = () => (
    <div className="space-y-4">
      <div className="pb-4">
        <h3 className="text-xl font-semibold text-gray-800">{t('viewFinance.expenses.title')}</h3>
        <p className="text-sm text-gray-600">{t('viewFinance.expenses.subtitle')}</p>
      </div>
      <div className="bg-white -lg shadow p-6 space-y-4">
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <strong className="text-gray-700">{t('viewFinance.expenses.maintenanceCosts')}</strong>
          <span className="font-medium text-gray-900">
            ${financialData?.expenses?.maintenance_costs?.toLocaleString() || 0}
          </span>
        </div>
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <strong className="text-gray-700">{t('viewFinance.expenses.operatingCosts')}</strong>
          <span className="font-medium text-gray-900">
            ${financialData?.expenses?.operating_costs?.toLocaleString() || 0}
          </span>
        </div>
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <strong className="text-gray-700">{t('viewFinance.expenses.insuranceCosts')}</strong>
          <span className="font-medium text-gray-900">
            ${financialData?.expenses?.insurance_costs?.toLocaleString() || 0}
          </span>
        </div>
        <div className="flex justify-between items-center pt-2">
          <strong className="text-gray-800 font-semibold">{t('viewFinance.expenses.totalAnnual')}</strong>
          <span className="font-bold text-gray-900">
            ${financialData?.expenses?.total_annual_expenses?.toLocaleString() || 0}
          </span>
        </div>
      </div>
    </div>
  );

  const renderROIReport = () => (
    <div className="space-y-4">
      <div className="pb-4">
        <h3 className="text-xl font-semibold text-gray-800">{t('viewFinance.roi.title')}</h3>
        <p className="text-sm text-gray-600">{t('viewFinance.roi.subtitle')}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white -lg shadow p-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">{t('viewFinance.roi.totalExpenses')}</h4>
            <span className="text-lg font-bold text-gray-900">
              ${financialData?.summary?.total_expenses?.toLocaleString() || 0}
            </span>
          </div>
        </div>

        <div className="bg-white -lg shadow p-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">{t('viewFinance.roi.averageCost')}</h4>
            <span className="text-lg font-bold text-gray-900">
              ${financialData?.summary?.average_cost_per_asset?.toLocaleString() || 0}
            </span>
          </div>
        </div>

        <div className="bg-white -lg shadow p-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-1">{t('viewFinance.roi.totalAssets')}</h4>
            <span className="text-lg font-bold text-gray-900">
              {financialData?.summary?.total_assets || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    {
      id: 'depreciation',
      label: t('viewFinance.tabs.depreciation'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'expenses',
      label: t('viewFinance.tabs.expenses'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'roi',
      label: t('viewFinance.tabs.roi'),
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    }
  ];

  const getTabTitle = () => {
    switch (activeTab) {
      case 'depreciation':
        return t('viewFinance.titles.depreciation');
      case 'expenses':
        return t('viewFinance.titles.expenses');
      case 'roi':
        return t('viewFinance.titles.roi');
      default:
        return t('viewFinance.titles.default');
    }
  };

  if (!showModal) return null;

  return (
    <>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white -lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h2 className="text-2xl font-semibold text-gray-800">{getTabTitle()}</h2>
              <button 
                className="text-gray-500 hover:text-gray-700 focus:outline-none" 
                onClick={() => setShowModal(false)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-grow">
              {loading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin -full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="bg-red-50 text-red-600 p-4 -md">
                  {error}
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="flex border-b border-gray-200">
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          className={`flex items-center px-4 py-3 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === tab.id
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                          onClick={() => setActiveTab(tab.id)}
                        >
                          <span className="mr-2">{tab.icon}</span>
                          <span>{tab.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="tab-content">
                    {activeTab === 'depreciation' && renderDepreciationReport()}
                    {activeTab === 'expenses' && renderExpensesReport()}
                    {activeTab === 'roi' && renderROIReport()}
                  </div>
                </>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                className="px-4 py-2 bg-blue-500 text-white -md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors"
                onClick={() => setShowModal(false)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ViewFinance;